/* eslint-disable no-prototype-builtins */
import React, {
    useCallback, useContext, useEffect, useMemo, useState, useRef
} from 'react';
import {
    get as _get, isEmpty as _isEmpty, set as _set, isUndefined, findIndex as _findIndex
} from 'lodash';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from 'e1p-portals-wizard-react';
import { AccountService, AccountSearchUtil } from 'e1p-capability-gateway';
import { useAuthentication, withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { useModal } from '@jutro/components';
import { LoadSaveService } from 'e1p-capability-quoteandbind';
import { RandomNumberUtil, AddressUtil } from 'e1p-portals-util-js';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { commonMessages as e1pCommonMessages } from 'e1p-platform-translations';
import { AccountsFoundComponent } from 'e1p-capability-policyjob-react';
import { AmfamOktaTokenContext } from 'e1p-capability-gateway-react';
import appConfig from 'app-config';
import {
    useEffectiveDateUtil,
    useNewBusinessUtil,
    useSniUtil,
    e1pUSStatesUtil,
    useDataLayer
} from 'e1p-capability-hooks';
import moment from 'moment';
import messages from './PolicyDetailsPage.messages';
import metadata from './PolicyDetailsPage.metadata.json5';

const LOB = 'personalUmbrella_EU';
const pniPath = 'lobData.personalUmbrella_EU.primaryNamedInsured';
const experienceIdMap = {
    MSA: '7119', // always the same for MSA
    CONNECT: '7192' // This will change. It probably has to be an array and have an ID for each partner. Not sure yet.
}

function PolicyDetailsPage(props) {
    const modalApi = useModal();
    const {
        initialValidation,
        isComponentValid,
        onValidate,
        registerInitialComponentValidation,
        registerComponentValidation,
        disregardFieldValidation
    } = useValidation('PolicyDetailsPage');
    const {
        wizardData: submissionVM,
        updateWizardData,
        authUserData,
        isSkipping,
        updateWizardSnapshot
    } = props;
    const translator = useTranslator();
    const [locationAndProducerCodes, setLocationAndProducerCodes] = useState([]);
    const [producerCodeDetails, setProducerCodeDetails] = useState({});
    const [agencyName, setAgencyName] = useState(undefined);
    const [bookTransferIndicator, setBookTransferIndicator] = useState(undefined);
    const [serviceCenterIndicator, setServiceCenterIndicator] = useState(undefined);
    const [isPageSubmitted, updateIsPageSubmitted] = useState(false);
    const viewModelService = useContext(ViewModelServiceContext);
    const { authHeader } = useAuthentication();
    const [isAnExistingAccount, setIsAnExistingAccount] = useState(false);
    const [isStandardizingAddress, setIsStandardizingAddress] = useState(false);
    const [submissionExists, setSubmissionExists] = useState(false);
    const [submissionCreationInProgress, setSubmissionCreationInProgress] = useState(false);
    const [isComponentInitialized, setIsComponentInitialized] = useState(false);
    const hasPerformedAccountSearch = useRef(false);
    const [USStates, setUSStates] = useState([]);
    const [isSavingCurrentPageChanges, setIsSavingCurrentPageChanges] = useState(false);
    const [showSearchButtonErrors, setShowSearchButtonErrors] = useState(false);
    const policyState = _get(submissionVM, 'baseData.policyAddress.state.value.code');
    const sessionIdPrefixEdit = 'EU - NB - ';
    const sessionHeader = sessionIdPrefixEdit + RandomNumberUtil.randomFixedInteger(10);
    const [testRatingDate, setTestRatingDate] = useState(undefined);
    const [testQuoteCreationDate, setTestQuoteCreationDate] = useState(undefined);
    const environment = _get(appConfig, 'env.AMFAM_ENV', 'local');
    const { daysInFutureForBookroll, daysInFuture } = appConfig.dateInputConfig;
    const { opCo } = useContext(AmfamOktaTokenContext);
    const isConnectOpCo = opCo === 'CONNECT';
    const { operatingCompanyConfig } = appConfig;

    if (!_get(submissionVM, 'baseData.experienceID_Ext.value')) {
        _set(submissionVM, 'baseData.experienceID_Ext.value', experienceIdMap[opCo]);
    }

    const { addNewQuoteTagIfApplicable } = useDataLayer();

    useEffect(()=>{
        addNewQuoteTagIfApplicable(submissionVM);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[]);

    useEffect(() => {
        // Take the show errors off once page is fixed
        if (isComponentValid && isPageSubmitted) {
            updateIsPageSubmitted(false);
        }
    }, [submissionVM, isComponentValid, isPageSubmitted]);

    const isAgent = authUserData.roles_Ext.includes('ext_sales_service');
    const {
        handlePeriodStartDateChange,
        getDefaultPeriodEndDate,
        getEffectiveDateBoundsForSubmission
    } = useEffectiveDateUtil(submissionVM, updateWizardData);

    const {
        createAccountHolderVM,
        getLocationAndProducerCodes,
        startSubmission,
        createPrimaryNamedInsuredVM,
        getProducerDetailsFromProducerCodes
    } = useNewBusinessUtil(
        submissionVM,
        updateWizardData,
        viewModelService,
        LOB,
        isAnExistingAccount,
        authHeader,
        setLocationAndProducerCodes
    );

    const { createSecondaryNamedInsuredVM, removeSni } = useSniUtil(
        submissionVM,
        updateWizardData,
        viewModelService,
        LOB,
        disregardFieldValidation
    );

    const markAccountSearchCompleted = () => {
        hasPerformedAccountSearch.current = true;
    };

    const checkAcknowledgementsPresents = useCallback(() => {
        if (submissionVM.value.lobData.personalUmbrella_EU.acknowledgements === undefined) {
            return false;
        }

        return submissionVM.value.lobData.personalUmbrella_EU.acknowledgements.every(
            (acknowledgement) => acknowledgement.acknowledgeAnswerType === 'accept'
        );
    }, [submissionVM.value.lobData.personalUmbrella_EU.acknowledgements]);

    useEffect(() => {
        // Take the show errors off once page is fixed
        if (isComponentValid && isPageSubmitted && checkAcknowledgementsPresents()) {
            updateIsPageSubmitted(false);
        }
    }, [submissionVM, isComponentValid, isPageSubmitted, checkAcknowledgementsPresents]);

    useEffect(() => {
        const stateValues = e1pUSStatesUtil.getUSStates(viewModelService);

        setUSStates(stateValues);
        authHeader['afe-session-id'] = sessionHeader;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // If Book Transfer is Yes, allow quoting up to 120 days in the future, otherwise 60 days  
    const { maxEffectiveDate, minEffectiveDate, currentMinEffectiveDate } = useMemo(
        () => bookTransferIndicator ? getEffectiveDateBoundsForSubmission(daysInFutureForBookroll)
                : getEffectiveDateBoundsForSubmission(daysInFuture)
        ,
        [getEffectiveDateBoundsForSubmission, bookTransferIndicator, daysInFutureForBookroll, daysInFuture]
    );

    const generateOverrides = useCallback(() => {
        const overrideProps = {};

        overrideProps.secondaryNamedInsured = {
            hideSSN: true
        };

        return overrideProps;
    }, []);

    /**
     * Helper function to handle below scenario:
     * if insured residence address is entered and user clicks on search account
     * and choses existing account; to keep old addresses in place for
     * mailing and billing address
     */
    const setCorrectAddressAfterChosingExistingAccount = useCallback(
        (policyAddress) => {
            const mailingSameAsEnterred = AddressUtil.isSameAddress(
                policyAddress,
                _get(
                    submissionVM,
                    'baseData.policyMailingAddress_Ext.value'
                )
            );

            _set(
                submissionVM,
                'baseData.isMailingAddressSame_Ext.value',
                mailingSameAsEnterred
            );
        }, [submissionVM]
    );
    const setPNI = useCallback(
        (accountHolder) => {
            _set(submissionVM, `${pniPath}.person.value`, accountHolder);

            /**
                if insured residence address is entered and user clicks on search account
                and choses existing account; to keep old address in place for
                mailing and billing address calling fn setCorrectAddressAfterChosingExistingAccount
             */
            if (
                !!_get(submissionVM, 'baseData.policyAddress.addressLine1.value')
                && !!_get(submissionVM, 'baseData.policyAddress.city.value')
                && !!_get(submissionVM, 'baseData.policyAddress.state.value')
                && !!_get(submissionVM, 'baseData.policyAddress.postalCode.value')
            ) {
                setCorrectAddressAfterChosingExistingAccount(accountHolder.primaryAddress);
            }

            _set(submissionVM, 'baseData.policyAddress.value', accountHolder.primaryAddress);
            updateWizardData(submissionVM);
        },
        [setCorrectAddressAfterChosingExistingAccount, submissionVM, updateWizardData]
    );

    useEffect(() => {
        const accountHolderPath = 'baseData.accountHolder';
        const accountHolder = _get(submissionVM, `${accountHolderPath}.value`);
        const accountExists = !_isEmpty(accountHolder);

        if (!accountExists) {
            // Needed for account search
            createAccountHolderVM();
        }

        const pni = _get(submissionVM, `${pniPath}.value`);

        // Create PrimaryNamedInsured if necessary and populate it with accountHolder data
        if (_isEmpty(pni)) {
            createPrimaryNamedInsuredVM();

            if (accountExists) {
                _set(submissionVM, `${pniPath}.person.value`, accountHolder);

                // when transaction is not started, wizardPageHeader needs updated value
                // from PNI object to show the PNI Name in breadcrum
                // and renderContextComponent is using wizardsnapshot to read the values,
                // updateWizardData wont work here
                if (!_get(submissionVM, 'quoteID.value')) {
                    updateWizardSnapshot(submissionVM);
                }
            }
        }

        setIsAnExistingAccount(accountExists);

        // Set submission as existing (new quote for this account)
        if (_get(submissionVM, 'quoteID.value')) {
            setSubmissionExists(true);
        }
        // executed only once on initial render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [AccountService]);

    useEffect(() => {
        if (submissionVM.baseData.quoteSource_Ext.sourceType === undefined) {
            const model = {
                sourceType: 'directentry',
                sourceName: 'msaagentsonly'
            };

            submissionVM.baseData.quoteSource_Ext.value = model;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showAccountsModal = useCallback(async (vm) => {
        const componentProps = {
            title: 'Existing Accounts',
            iconClassType: false,
            showCloseBtn: false,
            showCancelBtn: true,
            accountsVM: vm
        };

        return modalApi.showModal(<AccountsFoundComponent {...componentProps} />);
    }, [modalApi]);

    const createNewAccountForSubmission = useCallback(async () => {
        // Create accountHolder from pni data for account creation
        const accountHolder = {
            person: _get(submissionVM, `${pniPath}.person.value`)
        };
        const addressForAccountCreation = _get(submissionVM, 'baseData.policyAddress.value');

        // since we are showing policy address on UI,
        // we need to create account with that address only
        // thats why we are copying policyAddress in person.primary address
        _set(accountHolder, 'person.primaryAddress', addressForAccountCreation);

        const accountHolderViewVM = viewModelService.create(
            accountHolder,
            'pc',
            'amfam.edge.capabilities.policycommon.accountcontact.dto.AccountHolderDTO',
            {
                ProducerCodeRequired: true
            }
        );
        const newAccount = {};

        newAccount.accountHolder = _get(accountHolderViewVM, 'value');

        // E1PAP1PC-12445
        // Setting selected producer code to account.producerCodes
        // to assign correct producer code to account
        const selectedProducerCode = locationAndProducerCodes?.producerCodes?.filter(
            (producerCode) => producerCode.name
                === submissionVM.value.baseData.producerCode_Ext
        );

        newAccount.producerCodes = selectedProducerCode;

        // If user is underwriter; structure for locationAndProducerCodes is different
        if (!newAccount.producerCodes) {
            const { producerCodes } = locationAndProducerCodes.find(
                (codeObject) => codeObject.producerCodes.name
                    === submissionVM.value.baseData.producerCode_Ext
            );

            newAccount.producerCodes = [producerCodes];
        }

        const expID = _get(submissionVM, 'baseData.experienceID_Ext.value');

        // expID is if it already exists on base data; came in from a url param bridge
        // experienceIdMap is for direct entry; new quote+new account
        newAccount.experienceID = expID || experienceIdMap[opCo];

        const newAccountResponse = await AccountService.getNewAccountForContact(
            newAccount,
            authHeader
        );

        if (newAccountResponse.accountNumber) {
            _set(
                submissionVM,
                'baseData.accountNumber.value',
                newAccountResponse.accountNumber
            );
            setIsAnExistingAccount(true);
        } else {
            modalApi.showConfirm({
                status: 'warning',
                icon: 'mi-error-outline',
                title: messages.accountTypeErrorMessage,
                message: messages.newAccountCreateErrorMessage,
                confirmButtonText: e1pCommonMessages.confirm,
                cancelButtonText: e1pCommonMessages.cancel
            }).then(() => {
                setSubmissionCreationInProgress(false);
                window.history.back();
            });
        }
    }, [authHeader, locationAndProducerCodes, opCo, submissionVM, viewModelService, modalApi]);

    /**
    * Setting mailingAddress and billingAddressValues based on indicators
    */
    const setCorrectAddressValues = useCallback(
        () => {
            // if mailingAddressSameInd is true then set policyAddress value to mailingAddress
            if (
                _get(
                    submissionVM,
                    'baseData.isMailingAddressSame_Ext.value'
                )
            ) {
                _set(
                    submissionVM,
                    'baseData.policyMailingAddress_Ext.value',
                    _get(submissionVM, 'baseData.policyAddress.value')
                );
            }

            //  if billingAddressSameInd is true then set mailingAddress value to billingAddress
            if (
                _get(
                    submissionVM,
                    'baseData.isBillingAddressSame_Ext.value'
                )
            ) {
                _set(
                    submissionVM,
                    'baseData.policyBillingAddress_Ext.value',
                    _get(submissionVM, 'baseData.policyMailingAddress_Ext.value')
                );
            }
        }, [submissionVM]
    );

    /**
     * since user can now edit the policy Address(insured residence information),
     * so after creation of job, we need to use the address entered by user or which is
     * available in submissionVM for policy address, and need to sync it to other addresses as well
     */
    const setPolicyMailingBillingAdressValuesAfterSubmissionCreation = useCallback(
        (submission) => {
            const policyAddressOnVM = _get(submissionVM, 'baseData.policyAddress.value');

            _set(submission, 'baseData.policyAddress', policyAddressOnVM);

            const mailingSameAsEnterred = AddressUtil.isSameAddress(
                _get(
                    submission,
                    'baseData.policyAddress'
                ),
                _get(
                    submissionVM,
                    'baseData.policyMailingAddress_Ext.value'
                )
            );
            const billingSameAsEnterred = AddressUtil.isSameAddress(
                _get(
                    submissionVM,
                    'baseData.policyMailingAddress_Ext.value'
                ),
                _get(
                    submissionVM,
                    'baseData.policyBillingAddress_Ext.value'
                )
            );

            _set(
                submission,
                'baseData.isBillingAddressSame_Ext',
                billingSameAsEnterred
            );
            _set(
                submission,
                'baseData.isMailingAddressSame_Ext',
                mailingSameAsEnterred
            );
            _set(
                submission,
                'baseData.policyBillingAddress_Ext',
                submissionVM.baseData.policyBillingAddress_Ext.value
            );
            _set(
                submission,
                'baseData.policyMailingAddress_Ext',
                submissionVM.baseData.policyMailingAddress_Ext.value
            );
        }, [submissionVM]
    );

    const onNext = useCallback(async () => {
        const shouldGoNext = isComponentValid && checkAcknowledgementsPresents();

        if (!shouldGoNext) {
            updateIsPageSubmitted(true);
            window.scrollTo(0, 0);

            return false;
        }

        setSubmissionCreationInProgress(true);
        setCorrectAddressValues();

        const priorAddresses = submissionVM.lobData.personalUmbrella_EU
            .primaryNamedInsured?.priorAddresses?.value;

        if (!isAnExistingAccount || !submissionExists) {
            if (!isAnExistingAccount) {
                // If user hasn't searched for the account, perform search
                // Do an automatic search
                if (!hasPerformedAccountSearch.current) {
                    const accountHolder = {
                        person: _get(submissionVM, `${pniPath}.person.value`)
                    };

                    _set(
                        accountHolder,
                        'person.primaryAddress',
                        submissionVM.baseData.policyAddress.value
                    );

                    const connectConfigObject = operatingCompanyConfig[opCo].experiences;
                    let partnerCode;

                    for (const key in connectConfigObject) {
                        if (connectConfigObject.hasOwnProperty(key)) {
                            if (connectConfigObject[key].experienceId === _get(submissionVM, 'baseData.experienceID_Ext.value')) {
                                partnerCode = key;

                                if (connectConfigObject[key].retired && connectConfigObject[key].mergedPartnerCode) {
                                    partnerCode = connectConfigObject[key].mergedPartnerCode;
                                }
                            }
                        }
                    }

                    const response = await AccountSearchUtil.lookupAccount(
                        accountHolder.person,
                        submissionVM.baseData.value.producerCode_Ext,
                        partnerCode,
                        authHeader, viewModelService
                    );

                    try {
                        // user selected an account
                        const { chosenAccount } = await showAccountsModal(response);

                        if (chosenAccount.accountHolder) {
                            submissionVM.baseData.accountHolder.value = chosenAccount
                                .accountHolder.person;
                            submissionVM.baseData.accountNumber.value = chosenAccount.accountNumber;
                            submissionVM.baseData.policyAddress.value = chosenAccount
                                .accountHolder.person.primaryAddress;
                            setIsAnExistingAccount(true);
                        } else {
                            // Create new account when user selects Create New Account
                            // Button/Radio Button
                            await createNewAccountForSubmission();
                        }
                    } catch {
                        // User clicks on Cancel button to stop the creation of Account
                        setSubmissionCreationInProgress(false);

                        return false;
                    }
                } else {
                    // User has searched manually and wants to use a new account
                    await createNewAccountForSubmission();
                }

                const submission = await startSubmission({ testQuoteCreationDate, testRatingDate });

                setPolicyMailingBillingAdressValuesAfterSubmissionCreation(submission);
                _set(submission, 'baseData.quoteSource_Ext', {
                    sourceType: 'directentry',
                    sourceName: 'msaagentsonly'
                });
                _set(
                    submission,
                    `lobData[${LOB}].secondaryNamedInsured`,
                    submissionVM.lobData[LOB].secondaryNamedInsured.value
                );
                _set(
                    submission,
                    `lobData[${LOB}].acknowledgements`,
                    submissionVM.lobData[LOB].acknowledgements.value
                );
                _set(
                    submission,
                    `lobData[${LOB}].acknowledgements`,
                    submissionVM.lobData[LOB].acknowledgements.value
                );
                _set(
                    submission,
                    `lobData[${LOB}].primaryNamedInsured`,
                    submissionVM.lobData[LOB].primaryNamedInsured.value
                );

                submission.lobData.personalUmbrella_EU.primaryNamedInsured
                    .priorAddresses = priorAddresses;
                _set(submissionVM, 'value', submission);

                if (!_isEmpty(_get(submissionVM, 'value.baseData.producerCode_Ext'))) {
                    _set(
                        submissionVM,
                        'baseData.value.producerCode',
                        _get(submissionVM, 'value.baseData.producerCode_Ext')
                    );
                }

                // Need this for validation rules
                submissionVM.flowStepIDs_Ext.value = ['namedinsured'];
                submissionVM.entryCompletionStepIDs_Ext.value = ['namedinsured'];

                const QuoteDataResponse = await LoadSaveService.updateDraftSubmission(
                    submissionVM.value,
                    authHeader
                );

                _set(submissionVM, 'value', QuoteDataResponse);

                if (_get(submissionVM, 'value.baseData.accountNumber')) {
                    setIsAnExistingAccount(true);
                }

                if (_get(submissionVM, 'value.quoteID')) {
                    setSubmissionExists(true);
                }

                setSubmissionCreationInProgress(false);

                return submissionVM;
            }

            // New quote for this account
            const submission = await startSubmission({ testQuoteCreationDate, testRatingDate });

            setPolicyMailingBillingAdressValuesAfterSubmissionCreation(submission);
            _set(submission, 'baseData.quoteSource_Ext', {
                sourceType: 'directentry',
                sourceName: 'msaagentsonly'
            });
            _set(
                submission,
                `lobData[${LOB}].secondaryNamedInsured`,
                submissionVM.lobData[LOB].secondaryNamedInsured.value
            );
            _set(
                submission,
                `lobData[${LOB}].acknowledgements`,
                submissionVM.lobData[LOB].acknowledgements.value
            );
            _set(
                submission,
                `lobData[${LOB}].primaryNamedInsured`,
                submissionVM.lobData[LOB].primaryNamedInsured.value
            );
            submission.lobData.personalUmbrella_EU
                .primaryNamedInsured.priorAddresses = priorAddresses;
            _set(submissionVM, 'value', submission);

            if (!_isEmpty(_get(submissionVM, 'value.baseData.producerCode_Ext'))) {
                _set(
                    submissionVM,
                    'baseData.value.producerCode',
                    _get(submissionVM, 'value.baseData.producerCode_Ext')
                );
            }

            // Need this for validation rules
            _set(submissionVM, 'flowStepIDs_Ext.value', ['namedinsured']);
            _set(submissionVM, 'entryCompletionStepIDs_Ext.value', ['namedinsured']);

            // Update draft with value from the form
            authHeader['afe-session-id'] = sessionIdPrefixEdit + _get(submissionVM, 'quoteID.value');

            const QuoteDataResponse = await LoadSaveService.updateDraftSubmission(
                submissionVM.value,
                authHeader
            );

            _set(submissionVM, 'value', QuoteDataResponse);

            if (_get(submissionVM, 'value.quoteID')) {
                setSubmissionExists(true);
            }

            setSubmissionCreationInProgress(false);

            return submissionVM;
        }

        _set(submissionVM, 'baseData.quoteSource_Ext.value', {
            sourceType: 'directentry',
            sourceName: 'msaagentsonly'
        });

        if (!_isEmpty(_get(submissionVM, 'value.baseData.producerCode_Ext'))) {
            _set(
                submissionVM,
                'baseData.value.producerCode',
                _get(submissionVM, 'value.baseData.producerCode_Ext')
            );
        }

        // Need this for validation rules
        submissionVM.flowStepIDs_Ext.value = ['namedinsured'];
        submissionVM.entryCompletionStepIDs_Ext.value = ['namedinsured'];
        authHeader['afe-session-id'] = sessionIdPrefixEdit + _get(submissionVM, 'quoteID.value');

        const QuoteDataResponse = await LoadSaveService.updateDraftSubmission(
            submissionVM.value,
            authHeader
        );

        _set(submissionVM, 'value', QuoteDataResponse);
        setSubmissionCreationInProgress(false);

        return submissionVM;
    }, [
        isComponentValid,
        checkAcknowledgementsPresents,
        setCorrectAddressValues,
        submissionVM,
        isAnExistingAccount,
        submissionExists,
        authHeader,
        startSubmission,
        setPolicyMailingBillingAdressValuesAfterSubmissionCreation,
        viewModelService,
        showAccountsModal,
        createNewAccountForSubmission,
        testQuoteCreationDate,
        testRatingDate,
        opCo,
        operatingCompanyConfig
    ]);

    const onSave = useCallback(
        async () => {
            setIsSavingCurrentPageChanges(true);

            try {
                await onNext();

                const fieldIssues = _get(submissionVM, 'value.errorsAndWarnings.validationIssues.fieldIssues', []);
                const exceptions = _get(submissionVM, 'baseData.exceptions_Ext.value', []);

                if (_isEmpty(fieldIssues) && _isEmpty(exceptions)) {
                    updateWizardSnapshot(submissionVM);
                }

                setIsSavingCurrentPageChanges(false);
            } catch {
                setIsSavingCurrentPageChanges(false);
            }
        }, [onNext, submissionVM, updateWizardSnapshot]
    );

    const doesSubmissionExist = useCallback(() => !!_get(submissionVM.value, 'quoteID'), [submissionVM]);


    const checkIsEffectiveDateValid = useCallback(
        () => {
            // If user does not have back date permissions and the policy effective date is 5 days past the current date then return false
            const effectiveDate = moment(submissionVM.baseData?.periodStartDate?.value);

            if (effectiveDate?.isBefore(moment().subtract(5, 'days'), 'd')
                && !(authUserData?.permissions_Ext.includes('increasedeffectivedaterange_ext') || authUserData.permissions_Ext.includes('unlimitedbackdate'))) {
                _set(submissionVM, 'transitioning', false);

                return false;
            }

            return true;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [submissionVM?.baseData?.periodStartDate?.value]
    );

    // IAP-808, user should land on insuredPage for copied quote
    // backend is resetting acknowledgements and sourceType to null/undefined
    const isUserAllowedToProceedForCopiedQuotes = useCallback(() => {
        const sourceTypeCondition = (_get(submissionVM, 'baseData.quoteSource_Ext.sourceType.value') === undefined
            || _get(submissionVM, 'baseData.quoteSource_Ext.sourceType.value.code') === 'directentry');

        if (sourceTypeCondition) {
            return _get(submissionVM, 'value.lobData.personalUmbrella_EU.acknowledgements.length') > 0
                && checkAcknowledgementsPresents();
        }

        return true;
    }, [checkAcknowledgementsPresents, submissionVM]);

    const isPageValid = useCallback(() => doesSubmissionExist() && checkIsEffectiveDateValid() && isUserAllowedToProceedForCopiedQuotes(),
        [checkIsEffectiveDateValid, doesSubmissionExist, isUserAllowedToProceedForCopiedQuotes]);

    useEffect(() => {
        registerInitialComponentValidation(isPageValid);
        registerComponentValidation(() => checkAcknowledgementsPresents() && checkIsEffectiveDateValid());
    }, [
        checkIsEffectiveDateValid,
        isPageValid,
        checkAcknowledgementsPresents,
        registerComponentValidation,
        registerInitialComponentValidation
    ]);

    const handleAddressValueChange = useCallback(
        (newVal, localPath) => {
            _set(submissionVM, localPath, newVal);
            updateWizardData(submissionVM);
        },
        [submissionVM, updateWizardData]
    );

    const handleProducerApiRespone = useCallback(
        (producerDetails) => {
            const {
                producerCode, address, phone, name, externalID, bookRollIndicator, serviceCenterIndicatorValue
            } = producerDetails;

            setLocationAndProducerCodes(
                [
                    {
                        producerCodes: { code: producerCode, name: producerCode },
                        locationCodes: { code: externalID, name: externalID }
                    }
                ]
            );
            _set(submissionVM, 'baseData.producerCode_Ext', producerCode);
            _set(submissionVM, 'baseData.externalID_Ext.value', externalID);
            setProducerCodeDetails({ producerCode, address, phone });
            setAgencyName(name);
            setBookTransferIndicator(bookRollIndicator);
            setServiceCenterIndicator(serviceCenterIndicatorValue);
        },
        [submissionVM],
    );

    const resolvers = {
        resolveCallbackMap: {
            onAddSni: createSecondaryNamedInsuredVM,
            onValidate,
            onRemoveSni: removeSni,
            onMarkAccountSearchCompleted: markAccountSearchCompleted,
            onHandleProducerApiResponse: handleProducerApiRespone
        }
    };

    const handleAcknowledgementsValueChange = useCallback(
        (value) => {
            _set(submissionVM, `lobData[${LOB}].acknowledgements`, value);
            updateWizardData(submissionVM);
        },
        [updateWizardData, submissionVM]
    );

    /**
     * Helper memo for dynamically generating the loading indicator message.
     */
    const getLoadingIndicatorMessage = useMemo(() => {
        let loadingMessage = '';

        if (isStandardizingAddress) {
            loadingMessage = translator(messages.standardizingAddressMessage);
        } else if (isSavingCurrentPageChanges) {
            loadingMessage = translator(e1pCommonMessages.savingCurrentPageChanges);
        } else if (isSkipping) {
            loadingMessage = translator(messages.loadingNextPageMessage);
        } else {
            loadingMessage = translator(messages.creatingYourSubmissionMessage);
        }

        return loadingMessage;
    }, [isStandardizingAddress, isSavingCurrentPageChanges, isSkipping, translator]);


    /**
     * Helper for getting producer code on initial render and
     * then when state changes. Will only run when a state changes
     * and on initial render so no need to make a callback as the param
     * will always be a new value.
     * @param {String} location State code
     * @param {Boolean} initialCallForExistingAccount
     */
    const getAndSetLocationCodes = async (location, initialCallForExistingAccount = false) => {
        const foundLocationCodes = await getLocationAndProducerCodes(location);

        // If only one code, set it as selected
        if (foundLocationCodes && foundLocationCodes.producerCodes.length === 1) {
            if(isConnectOpCo) {
                _set(submissionVM, 'baseData.producerCode_Ext', foundLocationCodes.producerCodes[0].code);
            } else {
                _set(submissionVM, 'baseData.producerCode_Ext', foundLocationCodes.producerCodes[0].code);
                _set(submissionVM, 'baseData.externalID_Ext', foundLocationCodes.locationCodes[0].code);
            }
        } else if (!initialCallForExistingAccount) {
            // dont want to reset below data on continue quote
            // or when user is creating new quote for existing account
            _set(submissionVM, 'baseData.producerCode_Ext', undefined);
            _set(submissionVM, 'baseData.externalID_Ext.value', undefined);
            setProducerCodeDetails({});
            setAgencyName(undefined);
            setBookTransferIndicator(undefined);
            setServiceCenterIndicator(undefined);
        }
    };

    /**
     * Initialize the producer code for an existing account
     *  as policy state will already exist
     */
    useEffect(() => {
        // Policy state will not exist for new account so nothing will happen
        // If New quote for an account we will use accountholders state to set
        // policy state and that state here will set the initial producer code
        if (!isSkipping && policyState && ( isAgent || isConnectOpCo )) {
            // only look up for agents
            getAndSetLocationCodes(policyState, true).then(() => {
                setIsComponentInitialized(true);
            }).catch(() => setIsComponentInitialized(true));
        } else {(setIsComponentInitialized(true));}
        // Only run on initial render. Helper will be called again from
        //   the state fields onChange handler
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSkipping]);

    /**
     * Since our risk State field is editable, so we need to
     * reset the policy address with selectedState
     */
    const resetPolicyAddress = useCallback((selectedState) => {
        const { _xCenter, _dtoName } = _get(submissionVM, 'baseData.policyAddress');
        const policyAddressVM = viewModelService.create({
            state: selectedState
        }, _xCenter, _dtoName);

        return policyAddressVM.value;
    }, [submissionVM, viewModelService]);

    /**
     * On change handler for the policy state field
     * This function should be used for most of the
     *   logic that should run when the state value changes.
     * Side effects should not be used as a replacement for
     *   on value changes. This page needs to be refactored.
     */
    const onPolicyStateChange = useCallback(
        async (newValue, path) => {
            // Set state on the VM
            _set(submissionVM, path, newValue);
            // Reset policy address
            _set(submissionVM, 'baseData.policyAddress.value', resetPolicyAddress(newValue));
            // Update data on the wizard component
            updateWizardData(submissionVM);

            // Get Producer codes for the state
            if (newValue !== undefined && ( isAgent || isConnectOpCo )) {
                // only for agents
                await getAndSetLocationCodes(newValue);
                // Because above function needs a refresh
                updateWizardData(submissionVM);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [getLocationAndProducerCodes, submissionVM, updateWizardData]
    );

    const isProducerCodeDisabled = useMemo(() => {
        if (locationAndProducerCodes.producerCodes
            && locationAndProducerCodes.producerCodes.length === 1) {
            _set(submissionVM, 'baseData.producerCode_Ext', locationAndProducerCodes.producerCodes[0].code);
            _set(submissionVM, 'baseData.externalID_Ext', locationAndProducerCodes.locationCodes[0].code);

            return true;
        }

        return false;
    }, [locationAndProducerCodes, submissionVM]);

    const locationCodeChange = useCallback(
        (value) => {
            _set(submissionVM, 'baseData.externalID_Ext', value);

            const selectedProducerCode = locationAndProducerCodes?.producerCodes
                ?.find((producerCode) => producerCode?.externalId_Ext === value);

            _set(submissionVM, 'baseData.producerCode_Ext', selectedProducerCode?.code);
            updateWizardData(submissionVM);
        },
        [submissionVM, updateWizardData, locationAndProducerCodes]
    );

    useEffect(() => {
        // Don't hit any apis if this is a continue quote
        if (isSkipping) {return;}

        const {
            producerCode
        } = producerCodeDetails;
        const existingProducerCode = _get(
            submissionVM, 'value.baseData.producerCode_Ext',
            _get(submissionVM, 'value.baseData.producerCode')
        );

        if (existingProducerCode && isAgent
            && existingProducerCode !== producerCode) {
            (async () => {
                // only for agents
                const details = await getProducerDetailsFromProducerCodes(
                    existingProducerCode,
                    locationAndProducerCodes.producerCodes,
                    submissionVM.value.baseData.externalID_Ext
                );

                // in case we dont get any response from service
                if (details) {
                    setProducerCodeDetails(details);
                    setAgencyName(details.agencyName);
                    setBookTransferIndicator(details.bookTransferIndicator);
                    setServiceCenterIndicator(details.serviceCenterIndicatorValue);
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submissionVM.baseData.producerCode_Ext.value, isSkipping]);

    useEffect(() => {
        /**
         * TODO:IAP-4140 need to remove this logic once the ProducerAPI is fixed from backend for CONNECT and OPCO dropdown is removed from page Header
         * 
         * Description: clear the exception if the producercode is available, this is required when 
         * user selected toggle as Connect and started new quote and on change of state user will get the exception
         * then user selected toggle as MSA, and provide location code, which will fetch the producercode, 
         * but we will be still showing the exception, that we need to remove once we have producerCode.
         */
        if(!isSkipping) {
            const existingProducerCode = _get(
                submissionVM, 'value.baseData.producerCode_Ext',
                _get(submissionVM, 'value.baseData.producerCode')
            );
            const exceptions = _get(submissionVM, 'value.baseData.exceptions_Ext', []);
            const isShowingExceptionForProducerCode = _findIndex(exceptions, { errorMessage: translator(e1pCommonMessages.genericErrorText) }) > -1;

            if(existingProducerCode && isShowingExceptionForProducerCode && exceptions.length === 1 ){
                _set(submissionVM, 'value.baseData.exceptions_Ext', []);
                updateWizardData(submissionVM);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submissionVM.baseData.producerCode_Ext.value, submissionVM.baseData.exceptions_Ext.value, isSkipping]);


    // used to show/hide wholepage loader and bottom navigation buttons as well
    const isPageLoaded = useMemo(() => !isStandardizingAddress
        && !submissionCreationInProgress
        && !isSkipping
        && !isSavingCurrentPageChanges,
        [isSavingCurrentPageChanges, isSkipping, isStandardizingAddress, submissionCreationInProgress]);

    /**
     * Define property overrides for this Jutro component.
     */
    const overrideProps = {
        '@field': {
            labelPosition: 'top',
            showRequired: true,
            showErrors: isPageSubmitted,
            autoComplete: false
        },
        '@container': {
            showErrors: isPageSubmitted
        },
        policyDetailsPageLoadingIndicator: {
            loaded: isPageLoaded,
            text: getLoadingIndicatorMessage
        },
        policyDetailsPageContainer: {
            visible:
                !submissionCreationInProgress
                && !isSkipping
                && !isSavingCurrentPageChanges,
        },
        coverageStartDate: {
            updateDateDto: handlePeriodStartDateChange,
            dateDTO: submissionVM.baseData.periodStartDate,
            defaultToToday: true,
            minDate: minEffectiveDate,
            maxDate: maxEffectiveDate,
            showErrors: isPageSubmitted || !isUndefined(_get(submissionVM.value, 'quoteID')),
            currentMinDate: currentMinEffectiveDate
        },
        coverageEndDate: {
            dateDTO: submissionVM.baseData.periodEndDate,
            updateDateDto: () => updateWizardData(submissionVM),
            readOnly: true,
            defaultValue: getDefaultPeriodEndDate
        },
        personalInfoContainer: {
            columns: ['0.25fr', '0.25fr', '0.25fr']
        },
        locationCode: {
            availableValues: locationAndProducerCodes.locationCodes,
            disabled: isProducerCodeDisabled,
            onValueChange: locationCodeChange,
            visible: isAgent,
            showErrors: isPageSubmitted || showSearchButtonErrors
        },
        ExternalProducerDetailsComponent: {
            visible: !isAgent,
            defaultValue: submissionVM.baseData.externalID_Ext?.value,
            showErrors: isPageSubmitted || showSearchButtonErrors
        },
        agencyName: {
            value: agencyName
        },
        bookTransferIndicator: {
            value: bookTransferIndicator
        },
        serviceCenterIndicator: {
            value: serviceCenterIndicator
        },
        sniContainer: {
            visible: !!_get(submissionVM, `lobData[${LOB}].secondaryNamedInsured.value`)
        },
        removeSni: {
            visible: !!_get(submissionVM, `lobData[${LOB}].secondaryNamedInsured.value`)
        },
        primaryNamedInsured: {
            hideSSN: true,
            showErrors: isPageSubmitted,
            isPNI: true
        },
        addSni: {
            visible: !_get(submissionVM, `lobData[${LOB}].secondaryNamedInsured.value`)
        },
        secondaryNamedInsured: {
            visible: !!_get(submissionVM, `lobData[${LOB}].secondaryNamedInsured.value`),
            hideSSN: true,
            showErrors: isPageSubmitted
        },
        changePniComponent: {
            accountNumber: _get(submissionVM, 'baseData.accountNumber.value'),
            authHeader,
            visible: !!_get(submissionVM, 'baseData.accountNumber.value'),
            excludedContacts: [
                _get(submissionVM, `lobData[${LOB}].primaryNamedInsured.person.publicID.value`, ''),
                _get(submissionVM, `lobData[${LOB}].secondaryNamedInsured.person.publicID.value`, '')
            ]
        },
        changeSniComponent: {
            accountNumber: _get(submissionVM, 'baseData.accountNumber.value'),
            authHeader,
            visible: !!_get(submissionVM, 'baseData.accountNumber.value')
                && !!_get(submissionVM, `lobData[${LOB}].secondaryNamedInsured.value`),
            excludedContacts: [
                _get(submissionVM, `lobData[${LOB}].primaryNamedInsured.person.publicID.value`, ''),
                _get(submissionVM, `lobData[${LOB}].secondaryNamedInsured.person.publicID.value`, '')
            ]
        },
        mailingAndBillingAddressComponent: {
            transactionVM: submissionVM,
            updateWizardData,
            onValidate,
            lob: LOB
        },
        producerAddressComponent: {
            address: producerCodeDetails?.address,
            phone: producerCodeDetails?.phone
        },
        e1pAcknowledgementComponent: {
            acknowledgements: _get(submissionVM, `lobData[${LOB}].acknowledgements`),
            policyState: {
                code: _get(submissionVM, 'baseData.policyAddress.state.value.code'),
                name: translator({ id: _get(submissionVM, 'baseData.policyAddress.state.value.name') })
            },
            onValueChange: handleAcknowledgementsValueChange,
            showErrors: isPageSubmitted && !checkAcknowledgementsPresents(),
            lob: LOB
        },
        policyState: {
            availableValues: e1pUSStatesUtil.getStateValues(USStates, translator),
            onValueChange: onPolicyStateChange
        },
        accountSearchComponent: {
            viewModelService,
            setIsAnExistingAccount,
            setPNI,
            authHeader,
            visible: !_get(submissionVM, 'baseData.accountNumber.value'),
            showSearchButtonErrors,
            setShowSearchButtonErrors,
            searchButtonErrorFunc: () => {
                setShowSearchButtonErrors(true);
                window.scrollTo(0, 0);

                return false;
            }
        },
        insuredResidenceAddress: {
            addressVM: _get(submissionVM, 'baseData.policyAddress'),
            labelPosition: 'top',
            showCountry: false,
            showOptional: false,
            onValidate,
            onAddressChange: (value, path) => handleAddressValueChange(value, `baseData.policyAddress.${path}`),
            viewOnlyMode: false,
            showParentLoader: setIsStandardizingAddress
        },
        EuRatingDateTestComponent: {
            testRatingDate,
            setTestRatingDate,
            testQuoteCreationDate,
            setTestQuoteCreationDate,
            visible: !_get(submissionVM, 'quoteID.value', false)
                && environment !== 'prod'
        },
        quoteCreationDate: {
            dateDTO: submissionVM.baseData.quoteCreationDate_Ext,
            updateDateDto: () => { },
            readOnly: true,
            visible: authUserData.permissions_Ext.includes('viewratefields_ext')
        },
        rateAsOfDate: {
            dateDTO: submissionVM.baseData.periodRateAsOfDate,
            updateDateDto: () => { },
            readOnly: true,
            visible: authUserData.permissions_Ext.includes('editrateasofdate')
        },
        ratingDate: {
            dateDTO: submissionVM.baseData.ratingDate,
            updateDateDto: () => { },
            readOnly: true,
            visible: authUserData.permissions_Ext.includes('viewratefields_ext')
        },
        agencyInformationPageContainer: {
            visible: opCo === 'MSA'
        },
        partnerInformationId: {
            visible: opCo === 'CONNECT',
            transactionVM: submissionVM,
            authHeader,
            LOB
        },
        ...generateOverrides()
    };

    const readValue = (id, path) => readViewModelValue(metadata.pageContent, submissionVM, id, path, overrideProps);

    if (!isComponentInitialized) {
        return null;
    }

    const isPageSubmittedWithErrors = isPageSubmitted
        && (!isComponentValid || !checkAcknowledgementsPresents());

    return (
        <WizardPage
            isLoadingWholePage={!isPageLoaded}
            onNext={onNext}
            showPrevious={false}
            skipWhen={initialValidation}
            onSave={onSave}
            showOnSave={!!_get(submissionVM, 'quoteID.value', false)}
            isPageSubmittedWithErrors={isPageSubmittedWithErrors || showSearchButtonErrors}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                resolveValue={readValue}
            />
        </WizardPage>
    );
}

PolicyDetailsPage.propTypes = wizardProps;
export default withAuthenticationContext(PolicyDetailsPage);
